/**
 * CurrencySelector — the app-wide display-currency dropdown.
 *
 * Reads the supported-currency set + the active currency from the shared
 * currency store (`useCurrency()`), so a change here instantly re-renders every
 * money view (plans, overage, credit packs, ads budgets…). Default is CAD.
 *
 * Self-contained (native <select> + design tokens) so it drops in anywhere:
 * the Subscription tab header, the Credits wallet toolbar, near the plan cards.
 */
import { useCurrency } from '@/stores/currency-store';
import { currencyLabel } from '@/lib/money';
import { useT, tr } from '@/i18n';

export interface CurrencySelectorProps {
  /** Optional visible label (default: "Devise"). Pass `null` to hide it. */
  label?: string | null;
  /** Extra classes for the wrapper. */
  className?: string;
  /** Compact variant (smaller padding) for dense toolbars. */
  compact?: boolean;
  id?: string;
}

export function CurrencySelector({
  label = tr('shell.components.currencySelector.currency'),
  className = '',
  compact = false,
  id = 'currency-selector',
}: CurrencySelectorProps) {
  const { t } = useT();
  const { currency, supported, setCurrency } = useCurrency();
  const pad = compact ? 'px-2 py-1 text-xs' : 'px-2 py-1.5 text-sm';

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {label != null && (
        <label htmlFor={id} className="text-xs font-medium text-muted">
          {label}
        </label>
      )}
      <select
        id={id}
        value={currency}
        onChange={(e) => setCurrency(e.target.value)}
        aria-label={label ?? t('shell.components.currencySelector.currency')}
        className={`rounded-token border border-border bg-surface ${pad} text-foreground focus:outline-none focus:ring-2 focus:ring-ring`}
      >
        {supported.map((code) => (
          <option key={code} value={code}>
            {currencyLabel(code)}
          </option>
        ))}
      </select>
    </div>
  );
}

export default CurrencySelector;
